import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AppWindow, ArrowDown, ArrowUp, ArrowUpDown, CheckSquare, Mail, Plus, Smartphone, Square, Workflow as WorkflowIcon, X } from 'lucide-react'
import { notiApi, type ChannelType, type ClientType, type Workflow } from '../lib/api-noti'
import { Toggle } from '../components/Toggle'

const WF_KEY = ['noti-workflows'] as const

type NameSort = 'none' | 'asc' | 'desc'

// The Workflows list normalises every source client and every destination into
// the 3 notification mediums from the TOR table (ข้อ Parameter): Web App /
// Mobile / E-mail. A row always renders all three; a medium is "checked" when at
// least one client/destination falls into it, with the names listed underneath.
type Medium = 'web' | 'mobile' | 'email'

const MEDIA: { id: Medium; label: string; Icon: typeof AppWindow }[] = [
  { id: 'web', label: 'Web App', Icon: AppWindow },
  { id: 'mobile', label: 'Mobile', Icon: Smartphone },
  { id: 'email', label: 'E-mail', Icon: Mail },
]

// Client type → medium (sources, and kind=client destinations). Backend services
// (lms/los/govpocket) and the admin portal are surfaced through the Web App.
function clientMedium(type: ClientType | undefined): Medium | null {
  switch (type) {
    case 'mobile': return 'mobile'
    case 'web': case 'portal': case 'service': return 'web'
    default: return null
  }
}

// Channel type → medium (kind=channel destinations). Push + SMS reach the
// customer's mobile; webhook is a system/web integration; mock is no medium.
function channelMedium(type: ChannelType | undefined): Medium | null {
  switch (type) {
    case 'email': return 'email'
    case 'fcm': case 'sms': return 'mobile'
    case 'webhook': return 'web'
    default: return null
  }
}

type MediumItem = { label: string; enabled?: boolean }

// Renders the fixed 3-button medium grid for one table cell. `all` shows all
// three as checked with a single note (used for "any source" workflows).
function MediumCell({ groups, all }: { groups: Partial<Record<Medium, MediumItem[]>>; all?: string }) {
  return (
    <div className="medium-cell">
      {MEDIA.map(({ id, label, Icon }) => {
        const items = groups[id] ?? []
        const on = all !== undefined || items.length > 0
        const Box = on ? CheckSquare : Square
        return (
          <div key={id} className={`medium-col ${on ? 'on' : 'off'}`}>
            <div className="medium-head">
              <Box size={14} className="medium-check" />
              <Icon size={12} />
              <span>{label}</span>
            </div>
            <div className="medium-names">
              {all !== undefined && id === 'web' && <span className="medium-name">{all}</span>}
              {items.map((it, i) => (
                <span key={i} className={`medium-name ${it.enabled === false ? 'dim' : ''}`}>{it.label}</span>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function WorkflowsPage() {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [nameSort, setNameSort] = useState<NameSort>('none')
  const { data, isLoading, isError, error } = useQuery({ queryKey: WF_KEY, queryFn: () => notiApi.listWorkflows() })
  const { data: clientsData } = useQuery({ queryKey: ['noti-clients'], queryFn: () => notiApi.listClients() })
  const { data: channelsData } = useQuery({ queryKey: ['noti-channels'], queryFn: () => notiApi.listChannels() })

  const wfMut = useMutation({
    mutationFn: (v: { key: string; patch: { active?: boolean; critical?: boolean } }) =>
      notiApi.patchWorkflow(v.key, v.patch),
    onSettled: () => qc.invalidateQueries({ queryKey: WF_KEY }),
  })

  if (isLoading) return <div className="panel muted">โหลด workflow…</div>
  if (isError) return <div className="panel error">{(error as Error).message}</div>

  // 'none' keeps the engine's order (seed order); asc/desc sort by Thai display name.
  const rows = data?.data ?? []
  const workflows = nameSort === 'none' ? rows : [...rows].sort((a: Workflow, b: Workflow) =>
    nameSort === 'asc' ? a.name.localeCompare(b.name, 'th') : b.name.localeCompare(a.name, 'th'))
  const SortIcon = nameSort === 'asc' ? ArrowUp : nameSort === 'desc' ? ArrowDown : ArrowUpDown

  // id → type lookups used to bucket sources/destinations into the 3 mediums.
  const clientTypes = new Map((clientsData?.data ?? []).map((c) => [c.id, c.type]))
  const channelTypes = new Map((channelsData?.data ?? []).map((c) => [c.id, c.type]))

  // Group a workflow's allowed source clients by medium.
  const sourceGroups = (w: Workflow): Partial<Record<Medium, MediumItem[]>> => {
    const g: Partial<Record<Medium, MediumItem[]>> = {}
    for (const id of w.allowedClients) {
      const m = clientMedium(clientTypes.get(id))
      if (m) (g[m] ??= []).push({ label: id })
    }
    return g
  }

  // Group a workflow's destinations by medium (client-kind by client type,
  // channel-kind by channel type); disabled destinations carry enabled:false.
  const destGroups = (w: Workflow): Partial<Record<Medium, MediumItem[]>> => {
    const g: Partial<Record<Medium, MediumItem[]>> = {}
    for (const d of w.destinations ?? []) {
      const m = d.kind === 'client' ? clientMedium(clientTypes.get(d.ref)) : channelMedium(channelTypes.get(d.ref))
      if (m) (g[m] ??= []).push({ label: d.ref, enabled: d.enabled })
    }
    return g
  }

  return (
    <>
      <div className="page-head">
        <h2><WorkflowIcon size={20} /> Workflows</h2>
        <button className="btn-primary" onClick={() => setCreateOpen(true)}><Plus size={16} /> สร้าง Workflow</button>
      </div>
      <p className="page-desc muted">
        Workflow คือข้อกำหนดการกระจายเหตุการณ์ของระบบแจ้งเตือน — ระบุว่า event นี้อนุญาตให้ระบบต้นทาง
        (client) ใดเป็นผู้ส่งเข้ามา จะถูกส่งต่อไปยังปลายทางใดบ้าง ทั้งกล่องแจ้งเตือนภายใน client app
        และช่องทางภายนอก (Email / SMS / Push / Webhook) รวมถึงพารามิเตอร์ที่ใช้ประกอบข้อความของแต่ละปลายทาง
        การปรับแก้มีผลทันทีในระดับ configuration โดยไม่ต้อง deploy ระบบใหม่
      </p>

      <div className="panel">
        <table className="data-table wf-table">
          <thead>
            <tr>
              <th aria-sort={nameSort === 'asc' ? 'ascending' : nameSort === 'desc' ? 'descending' : undefined}>
                <button className="th-sort" title="เรียงตามชื่อ workflow"
                  onClick={() => setNameSort(nameSort === 'none' ? 'asc' : nameSort === 'asc' ? 'desc' : 'none')}>
                  Workflow <SortIcon size={13} className={nameSort === 'none' ? 'sort-idle' : ''} />
                </button>
              </th><th>ต้นทางที่อนุญาต</th><th>ปลายทาง</th>
              <th style={{ textAlign: 'center' }}>active</th>
              <th style={{ textAlign: 'center' }}>critical</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {workflows.map((w: Workflow) => (
              <tr key={w.key} className={w.active ? '' : 'row-dim'}>
                <td>
                  <Link to="/workflows/$workflowKey" params={{ workflowKey: w.key }} className="row-link">
                    <div className="wf-name">{w.name}</div>
                    <code className="muted small">{w.key}</code>
                  </Link>
                </td>
                <td>
                  <MediumCell groups={sourceGroups(w)}
                    all={w.allowedClients.length === 0 ? 'ทุก client' : undefined} />
                </td>
                <td>
                  <MediumCell groups={destGroups(w)} />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <Toggle checked={w.active} disabled={wfMut.isPending}
                    onChange={(next) => wfMut.mutate({ key: w.key, patch: { active: next } })} />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <Toggle checked={w.critical} disabled={wfMut.isPending}
                    onChange={(next) => wfMut.mutate({ key: w.key, patch: { critical: next } })} />
                </td>
                <td>
                  <Link to="/workflows/$workflowKey" params={{ workflowKey: w.key }} className="btn-ghost">จัดการ</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <CreateWorkflowModal onClose={() => setCreateOpen(false)} onCreated={() => {
          setCreateOpen(false)
          qc.invalidateQueries({ queryKey: WF_KEY })
        }} />
      )}
    </>
  )
}

function CreateWorkflowModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [key, setKey] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [critical, setCritical] = useState(false)

  const create = useMutation({
    mutationFn: () => notiApi.createWorkflow({ key, name, description, critical }),
    onSuccess: onCreated,
  })

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>สร้าง Workflow ใหม่</h3>
          <button className="icon-ghost" onClick={onClose} aria-label="ปิด"><X size={16} /></button>
        </div>
        <div className="form-grid">
          <label>Workflow key
            <input value={key} onChange={(e) => setKey(e.target.value)} placeholder="loancard.kyc.reminder" spellCheck={false} />
            <span className="muted small">
              ตั้งตาม convention <code>&lt;domain&gt;.&lt;entity&gt;.&lt;event&gt;</code> —
              ระบบงานโดเมนใด.สิ่งที่เกี่ยวข้อง.เหตุการณ์ที่เกิด เช่น <code>loancard.contract.approved</code>,{' '}
              <code>govpocket.payment.completed</code> (ตัวพิมพ์เล็ก a–z/0–9 คั่นด้วยจุด)
              ระบบต้นทางใช้ key นี้ในการยิง trigger และไม่สามารถแก้ไขได้หลังสร้าง
            </span>
          </label>
          <label>ชื่อ
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="แจ้งเตือนต่ออายุ KYC" />
          </label>
          <label>คำอธิบาย
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="(ไม่บังคับ)" />
          </label>
          <label>Critical
            <div className="toggle-row">
              <Toggle checked={critical} onChange={setCritical} />
              <span className="muted small">ข้ามการปิดรับของผู้ใช้เสมอ</span>
            </div>
          </label>
        </div>
        {create.isError && <div className="error">{(create.error as Error).message}</div>}
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>ยกเลิก</button>
          <button className="btn-primary" disabled={!key || !name || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'กำลังสร้าง…' : 'สร้าง — ไปกำหนดปลายทาง'}
          </button>
        </div>
      </div>
    </div>
  )
}
